import { useApi } from '~/composables/api/useApi'

export function useUser() {
  const { fetchWithAuth } = useApi()
  const config = useRuntimeConfig()
  const RESOURCE = '/user'
  const configAPI = config.public.configAPI

  // GET Requests

  async function fetchUsersById(providedOptions: { userId: number }) {
    return await fetchWithAuth(
      `${RESOURCE}/${providedOptions.userId}`,
      'GET',
      {}
    )
  }

  async function findUser(providedOptions: {
    mail?: string
    firstName?: string
    lastName?: string
  }) {
    const data = await fetchWithAuth(`${RESOURCE}/list`, 'GET', {
      providedOptions: {
        user: providedOptions.mail,
        firstName: providedOptions.firstName,
        lastName: providedOptions.lastName,
      },
    })
    return data?.trpUser
  }

  async function fetchUserConfig(providedOptions: { userId: number }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${providedOptions.userId}`,
      'GET',
      {}
    )
  }

  async function fetchEditorSettings(providedOptions: { userId: number }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${providedOptions.userId}/editor`,
      'GET',
      {}
    )
  }

  async function fetchPageSettings(providedOptions: { userId: number }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${providedOptions.userId}/pages`,
      'GET',
      {}
    )
  }

  async function fetchDocumentSettings(providedOptions: { userId: number }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${providedOptions.userId}/documents`,
      'GET',
      {}
    )
  }

  async function fetchCollectionSettings(providedOptions: { userId: number }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${providedOptions.userId}/collections`,
      'GET',
      {}
    )
  }

  async function fetchRecognitionSettings(options: {
    userId?: number
    type: string
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/recognition/${options.type}`,
      'GET'
    )
  }

  async function fetchLastRecognitionModel(options: {
    userId?: number
    type: string
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/recognition/${options.type}/lastModel`,
      'GET'
    )
  }

  async function fetchLastRecognitionType(options: { userId?: number }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/recognition/lastType`,
      'GET'
    )
  }

  async function updateEditorSettings(options: {
    userId: number
    settings: any
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/editor`,
      'PUT',
      { data: options.settings }
    )
  }

  async function updatePageSettings(options: {
    userId: number
    settings: any
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/pages`,
      'PUT',
      { data: options.settings }
    )
  }

  async function updateDocumentSettings(options: {
    userId: number
    settings: any
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/documents`,
      'PUT',
      { data: options.settings }
    )
  }

  async function updateCollectionSettings(options: {
    userId: number
    settings: any
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/collections`,
      'PUT',
      { data: options.settings }
    )
    console.log('PUT collection settings', options)
  }

  async function updateRecognitionSettings(options: {
    userId?: number
    settings: any
    type: string
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/recognition/${options.type}`,
      'PUT',
      { data: options.settings }
    )
  }

  async function updateLastRecognitionModel(options: {
    userId?: number
    modelId: number
    type: string
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/recognition/${options.type}/lastModel`,
      'PUT',
      { data: { modelId: options.modelId } }
    )
  }

  async function updateLastRecognitionType(options: {
    userId?: number
    modelType: string
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/recognition/lastType`,
      'PUT',
      { data: { type: options.modelType } }
    )
  }

  // POST Requests

  async function updateUserConfig(providedOptions: {
    userId: number
    config: any
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${providedOptions.userId}`,
      'POST',
      {
        data: providedOptions.config,
      }
    )
  }

  async function createKeyboard(options: {
    userId?: number
    keyboard: any
    collId?: number
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/keyboards`,
      'POST',
      {
        data: options.keyboard,
        providedOptions: {
          collId: options.collId,
        },
      }
    )
  }

  async function getKeyboards(options: { userId?: number; collId?: number }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/keyboards`,
      'GET',
      {
        providedOptions: {
          collId: options.collId,
        },
      }
    )
  }

  async function updateKeyboard(options: {
    userId?: number
    keyboard: any
    collId?: number
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/keyboards/${options.keyboard.id}`,
      'PUT',
      {
        data: options.keyboard,
        providedOptions: {
          collId: options.collId,
        },
      }
    )
  }

  async function deleteKeyboard(options: {
    userId?: number
    keyboardId: number
    collId?: number
  }) {
    return await fetchWithAuth(
      `${configAPI}/v1/user/${options.userId}/keyboards/${options.keyboardId}`,
      'DELETE',
      {
        providedOptions: {
          collId: options.collId,
        },
      }
    )
  }

  async function getTags(options: { collId?: number; type?: string }) {
    return await fetchWithAuth(`${configAPI}/v1/tags`, 'GET', {
      providedOptions: { collId: options.collId, type: options.type },
    })
  }

  async function getAllTags(options: { collId?: number }) {
    return await fetchWithAuth(`${configAPI}/v1/allTags`, 'GET', {
      providedOptions: { collId: options.collId },
    })
  }

  async function getDefaultTags(options: { type?: string }) {
    return await fetchWithAuth(`${configAPI}/v1/tags/default`, 'GET', {
      providedOptions: { type: options.type },
    })
  }

  async function createTag(options: {
    collId: number
    type: string
    tag: any
  }) {
    return await fetchWithAuth(`${configAPI}/v1/tags`, 'POST', {
      providedOptions: { collId: options.collId, type: options.type },
      data: options.tag,
    })
  }

  async function deleteTag(options: {
    id: number
    collId: number
    type: string
  }) {
    return await fetchWithAuth(`${configAPI}/v1/tags`, 'DELETE', {
      providedOptions: {
        id: options.id,
        collId: options.collId,
        type: options.type,
      },
    })
  }

  async function updateTag(options: {
    id: number
    collId: number
    type: string
    tag: any
  }) {
    return await fetchWithAuth(`${configAPI}/v1/tags`, 'PUT', {
      providedOptions: {
        id: options.id,
        collId: options.collId,
        type: options.type,
      },
      data: options.tag,
    })
  }

  async function updateTagOrder(options: {
    tagOrder: any
    collId: number
    type: string
  }) {
    return await fetchWithAuth(`${configAPI}/v1/tags/order/update`, 'PUT', {
      data: options.tagOrder,
      providedOptions: { collId: options.collId, type: options.type },
    })
  }

  return {
    fetchUsersById,
    findUser,
    fetchUserConfig,
    updateUserConfig,
    fetchEditorSettings,
    fetchPageSettings,
    fetchDocumentSettings,
    fetchCollectionSettings,
    updateEditorSettings,
    updatePageSettings,
    updateDocumentSettings,
    updateCollectionSettings,
    updateRecognitionSettings,
    fetchRecognitionSettings,
    fetchLastRecognitionModel,
    updateLastRecognitionModel,
    fetchLastRecognitionType,
    updateLastRecognitionType,
    createKeyboard,
    getKeyboards,
    updateKeyboard,
    deleteKeyboard,
    getTags,
    getAllTags,
    createTag,
    deleteTag,
    updateTag,
    getDefaultTags,
    updateTagOrder,
  }
}
